import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styles from './page.module.css';
import AppBar from '../../../components/AppBar';
import { useUser } from '../../../context/UserContext';
import { useTheme } from '../../../context/ThemeProvider';
import ContentGrid from '../../../components/ContentGrid';
import { GlobalMicroButton } from '../../../components/GlobalMicroButton';
import GlobalTabs from '../../../components/GlobalTabs';
import AppFooter from '../../../components/AppFooter';
import { useLocale } from '../../../context/DynamicLocaleProvider';
import { useToast } from '../../../context/ToastProvider';
import { ProfileCard } from '../../../components/ProfileCard';
import { useDocuments } from '../../../hooks/useDocuments';
import { GlobalInput } from '../../../components/GlobalInput';
import { GlobalButton } from '../../../components/GlobalButton';
import { FamilyMember } from './types';
import {
  buildPagination as buildPaginationController,
  paginate as paginateController,
  fetchSponsorshipBadge as fetchSponsorshipBadgeController,
  getFamilyMembersForUser,
  getUserById,
  filterDocuments as filterDocumentsController,
  buildStatusOptions,
  buildCategoryOptions,
  fetchUserLanguage as fetchUserLanguageController,
  updateUserProfile
} from './controller';
import { getFirestore, doc as docRef, deleteDoc } from 'firebase/firestore';
import DoubleConfirmationModal from '../../../components/DoubleConfirmationModal';

export default function UserPage() {
  const { userId } = useParams<{ userId: string }>();
  const { user, firstName, lastName, avatar, company } = useUser();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLocale();
  const [targetUser, setTargetUser] = useState<FamilyMember | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [searchQuery] = useState('');
  const [statusFilter] = useState('');
  const [categoryFilter] = useState('');
  const [, setUserLanguage] = useState<string>('en');
  const [, setLoadingLanguage] = useState<boolean>(false);
  const [, setIsMobileView] = useState<boolean>(false);
  const [sponsorshipBadge, setSponsorshipBadge] = useState<{ enabled: boolean; text: string; logoUrl: string } | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const ITEMS_PER_PAGE = 12;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const showToast = useToast();

  // Settings form state (for target user)
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyEmail: ''
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const handleProfileFormChange = (field: keyof typeof profileForm, value: string) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
  };

  // Create a mock user object for the useDocuments hook to fetch documents for the target user
  const targetUserForDocuments = targetUser ? { uid: userId } as any : null;
  const { documents: userDocuments } = useDocuments(targetUserForDocuments);

  // Filter options for invoices (computed but not currently displayed)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const statusOptions = buildStatusOptions();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const categoryOptions = buildCategoryOptions();

  // Filter documents based on search and filters (computed but not currently displayed)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filteredDocuments = filterDocumentsController(userDocuments as any[], {
    search: searchQuery,
    status: statusFilter,
    category: categoryFilter,
  });

  // Pagination helpers (computed but not currently displayed)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const buildPagination = (totalItems: number, currentPage: number) =>
    buildPaginationController(totalItems, currentPage, ITEMS_PER_PAGE);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const paginate = <T,>(items: T[], page: number) =>
    paginateController<T>(items, page, ITEMS_PER_PAGE);

  // Fetch sponsorship badge and company name from user's company
  const fetchSponsorshipBadge = useCallback(async (userCompanyId: string) => {
    if (!userCompanyId || userCompanyId.trim() === '') {
      setCompanyName('');
      setSponsorshipBadge(null);
      return;
    }

    try {
      const result = await fetchSponsorshipBadgeController(userCompanyId);
      if (result) {
        setCompanyName(result.companyName || '');
        if (result.sponsorshipBadge) {
          setSponsorshipBadge(result.sponsorshipBadge);
        } else {
          setSponsorshipBadge(null);
        }
      } else {
        setCompanyName('');
        setSponsorshipBadge(null);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Error fetching sponsorship badge:', error);
      }
      setCompanyName('');
      setSponsorshipBadge(null);
    }
  }, []);

  // Sync settings form when target user changes
  useEffect(() => {
    if (!targetUser) return;
    // Get email: if viewing own profile, use user?.email, otherwise use targetUser.email
    const email = userId === user?.uid
      ? (user?.email || '')
      : ((targetUser as any).email || '');
    const nameParts = (targetUser.name || '').split(' ');
    const inferredFirst = nameParts[0] || '';
    const inferredLast = nameParts.slice(1).join(' ');
    setProfileForm({
      firstName: (targetUser as any).firstName || inferredFirst,
      lastName: (targetUser as any).lastName || inferredLast,
      email: email,
      companyEmail: (targetUser as any).companyEmail || ''
    });
  }, [targetUser, userId, user?.uid, user?.email]);

  const handleSaveProfile = async () => {
    if (!userId) return;
    setIsSavingProfile(true);
    try {
      await updateUserProfile(userId, profileForm);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to save user profile:', e);
      }
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Fetch target user data via controller
  const fetchTargetUser = useCallback(async () => {
    if (!userId || !user?.uid) return;

    setLoadingUser(true);
    try {
      // If viewing own profile
      if (userId === user.uid) {
        setTargetUser({
          id: user.uid,
          name: `${firstName && lastName ? `${firstName} ${lastName}` :
            user?.displayName ? user.displayName :
              user?.email ? user.email.split('@')[0] :
                'Current User'}`,
          status: 'active',
          avatar: avatar,
          email: user?.email || '',
          company: company || ''
        } as FamilyMember);

        // Load sponsorship badge if current user has a company
        if (company && company.trim() !== '') {
          await fetchSponsorshipBadge(company);
        } else {
          setCompanyName('');
          setSponsorshipBadge(null);
        }

        setLoadingUser(false);
        return;
      }

      const targetUserData = await getUserById(userId);
      if (!targetUserData) {
        navigate('/home');
        return;
      }
      setTargetUser(targetUserData);

      // Load sponsorship badge if user has a company
      if ((targetUserData as any).company && (targetUserData as any).company.trim() !== '') {
        await fetchSponsorshipBadge((targetUserData as any).company);
      } else {
        setCompanyName('');
        setSponsorshipBadge(null);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Error fetching target user:', error);
      }
      navigate('/home');
    } finally {
      setLoadingUser(false);
    }
  }, [userId, user?.uid, firstName, lastName, avatar, navigate, user?.displayName, user?.email, company, fetchSponsorshipBadge]);

  // Fetch family members from database via controller
  const fetchFamilyMembers = useCallback(async () => {
    if (!userId) return;
    try {
      const members = await getFamilyMembersForUser(userId);
      setFamilyMembers(members);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Error fetching family members:', error);
      }
      setFamilyMembers([]);
    }
  }, [userId]);

  // Fetch user language from database
  const fetchUserLanguage = useCallback(async () => {
    if (!userId) return;
    setLoadingLanguage(true);
    try {
      const lang = await fetchUserLanguageController(userId);
      setUserLanguage(lang);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Error fetching user language:', error);
      }
      setUserLanguage('en');
    } finally {
      setLoadingLanguage(false);
    }
  }, [userId]);

  // Fetch data when component mounts
  useEffect(() => {
    fetchTargetUser();
    fetchFamilyMembers();
    fetchUserLanguage();
  }, [fetchTargetUser, fetchFamilyMembers, fetchUserLanguage]);

  const handleDeleteUser = useCallback(async () => {
    if (!userId) return;
    setIsDeletingUser(true);
    try {
      const db = getFirestore();
      await deleteDoc(docRef(db, 'users', userId));
      showToast('User deleted', 'success');
      navigate('/home');
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to delete user:', e);
      }
      showToast('Failed to delete user', 'error');
    } finally {
      setIsDeletingUser(false);
      setShowDeleteModal(false);
    }
  }, [userId, navigate, showToast]);

  // Handle viewport width changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1024);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  if (loadingUser) {
    return (
      <div className={styles.container}>
        <AppBar />
      </div>
    );
  }

  if (!targetUser) {
    return (
      <div className={styles.container}>
        <AppBar />
        <ContentGrid rows='64px 180px 1fr 48px'>
          <div className={styles.loadingContainer}>
            <p>User not found</p>
          </div>
        </ContentGrid>
      </div>
    );
  }

  return (
    <div>

      {/* App bar */}
      <AppBar />

      {/* Content */}
      <ContentGrid rows='64px 136px 1fr 48px'>

        {/* Toolbar left side */}
        <div className={styles.homeToolbarLeft}>

          {/* Back button */}
          <div className={styles.backButton}>
            <GlobalMicroButton
              title={t('settings_navigate_back')}
              icon={<img src={isDarkMode ? "/assets/icons/chevron.left.svg" : "/assets/icons/chevron.left.black.svg"} alt="Left" width={8} height={8} />}
              onClick={() => navigate('/home?tab=users')}
            />
          </div>

          <b>{t('settings_profile_tab')}</b>
        </div>

        {/* Toolbar right side */}
        <div className={styles.homeToolbarRight}>
          {targetUser?.status === 'invited' && (
            <>

              {/* Delete user */}
              <GlobalMicroButton
                title={t('global_delete')}
                icon={<img src="/assets/icons/trash.red.svg" alt="Delete" width={16} height={16} />}
                onClick={() => setShowDeleteModal(true)}
                destructive
              >
                {t('global_delete')}
              </GlobalMicroButton>

              {/* Copy invitation link */}
              <GlobalMicroButton
                title={t('user_detail_copy_invitation_link') || 'Copy invitation link'}
                icon={<img src={isDarkMode ? "/assets/icons/document.on.document.svg" : "/assets/icons/document.on.document.black.svg"} alt="Copy link" width={16} height={16} />}
                onClick={() => {
                  if (!userId) return;
                  const url = `${window.location.origin}/signup?invitation=${userId}`;
                  navigator.clipboard.writeText(url).then(
                    () => showToast(t('user_detail_invitation_copied') || 'Invitation link copied', 'success'),
                    () => showToast(t('user_detail_invitation_copy_failed') || 'Failed to copy', 'error')
                  );
                }}
              >
                {t('user_detail_copy_invitation_link') || 'Copy invitation link'}
              </GlobalMicroButton>
            </>
          )}
        </div>

        {/* User Profile Container */}
        <div className={styles.userProfileContainer}>

          {/* User Profile */}
          <div className={styles.userProfileWrapper}>
            <ProfileCard
              firstName={targetUser.name?.split(' ')[0]}
              lastName={targetUser.name?.split(' ').slice(1).join(' ')}
              email={targetUser.email || (targetUser.id === user?.uid ? user?.email || undefined : undefined)}
              avatar={targetUser.avatar}
              status={targetUser.status || 'active'}
              role={targetUser.role}
              isDarkMode={isDarkMode}
            />
          </div>

          {/* Tabs */}
          <div className={styles.homeTabs}>
            {(() => {
              const searchParams = new URLSearchParams(location.search);
              const tabParam = searchParams.get('tab') || '';
              // Build tab keys - only settings tab now
              const tabKeys = ['settings'];
              const initialTabIndex = Math.max(0, tabKeys.indexOf(tabParam));
              const handleTabChange = (index: number) => {
                const params = new URLSearchParams(location.search);
                params.set('tab', tabKeys[index] || '');
                navigate({ search: params.toString() }, { replace: true });
              };
              return (
                <GlobalTabs
                  key={tabParam || 'default'}
                  initialIndex={initialTabIndex}
                  onChange={handleTabChange}
                  tabs={[
                    {
                      label: t('company_settings_tab'),
                      content: (
                        <div className={styles.profileSectionWrapper}>

                          {/* Profile form */}
                          <div className={styles.profileForm}>
                            <div className={styles.formGrid}>

                              {/* Personal information section */}
                              <div className={styles.formSection}>
                                <h4 className={styles.sectionTitle}>{t('settings_personal_information')}</h4>

                                <div className={styles.formRow}>

                                  {/* First name */}
                                  <GlobalInput
                                    label={t('settings_first_name')}
                                    value={profileForm.firstName}
                                    onChange={(e) => handleProfileFormChange('firstName', e.target.value)}
                                    className={styles.formInput}
                                  />

                                  {/* Last name */}
                                  <GlobalInput
                                    label={t('settings_last_name')}
                                    value={profileForm.lastName}
                                    onChange={(e) => handleProfileFormChange('lastName', e.target.value)}
                                    className={styles.formInput}
                                  />
                                </div>

                                {/* Email */}
                                <div className={styles.formRow}>
                                  <GlobalInput
                                    label={t('settings_email')}
                                    value={profileForm.email}
                                    onChange={(e) => handleProfileFormChange('email', e.target.value)}
                                    className={styles.formInput}
                                    disabled={true}
                                  />
                                </div>
                              </div>

                              {/* Save button */}
                              <div className={styles.formActions}>
                                <GlobalButton
                                  onClick={handleSaveProfile}
                                  isLoading={isSavingProfile}
                                  disabled={isSavingProfile}
                                >
                                  {isSavingProfile ? t('settings_saving') : t('settings_save_profile')}
                                </GlobalButton>
                              </div>
                            </div>
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />
              );
            })()}
          </div>
        </div>

        {/* Separator */}
        <div className={styles.gridSeparatorWrapper}>
          <div className={styles.gridSeparator} />
        </div>

        {/* Company Section */}
        {(companyName || sponsorshipBadge?.logoUrl) && (
          <div className={styles.sideSection}>
            <h2>{t('settings_your_company')}</h2>
            {companyName && (
              <p dangerouslySetInnerHTML={{ __html: t('settings_company_employee_text').replace('{companyName}', companyName) }}>
              </p>
            )}
            {sponsorshipBadge?.logoUrl && (
              <div className={styles.sponsorshipSection}>
                <div className={styles.sponsorLogo}>
                  <img
                    src={sponsorshipBadge.logoUrl}
                    alt="Corporate identity"
                    width={145}
                    height="auto"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <AppFooter inline />

      </ContentGrid>

      {/* Double confirmation modal for deleting user */}
      <DoubleConfirmationModal
        isOpen={showDeleteModal}
        title={`Delete "${targetUser?.name || t('settings_profile_tab')}"?`}
        paragraph={t('home_document_list_delete_document_confirmation_text')}
        primaryLabel={t('global_delete')}
        primaryIcon="/assets/icons/trash.red.svg"
        secondaryLabel={t('document_page_cancel')}
        secondaryIcon="/assets/icons/xmark.svg"
        onPrimary={handleDeleteUser}
        onSecondary={() => setShowDeleteModal(false)}
        primaryDestructive
        loading={isDeletingUser}
      />
    </div>
  );
}
